const STORAGE_KEY = "paw_signup_attribution";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
// Partner referral id from `?partner=<PARTNER_ID>`. Travels in the same
// attribution blob as the UTM tags, but is captured independently of them:
// a visitor who first arrived on a campaign link and only later clicked a
// partner link must still be attributed to that partner.
export const PARTNER_KEY = "partner";
const PARTNER_ID_MAX = 64;

type StoredAttribution = {
  data: Record<string, string>;
  expiresAt: number;
};

function readStored(): StoredAttribution | null {
  const existing = localStorage.getItem(STORAGE_KEY);
  if (!existing) return null;
  try {
    const parsed = JSON.parse(existing) as StoredAttribution;
    if (parsed.expiresAt <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function sanitizePartnerId(value: string | null): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > PARTNER_ID_MAX) return "";
  // Partner ids are uuids or short referral codes — anything else is noise.
  return /^[A-Za-z0-9_-]+$/.test(trimmed) ? trimmed : "";
}

export function captureSignupAttribution() {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const partner = sanitizePartnerId(params.get(PARTNER_KEY));
  const stored = readStored();

  // A live blob is kept as-is, except that a fresh partner link always wins:
  // last-touch attribution, so a partner who re-shares their link to an
  // already-tagged visitor still gets the credit.
  if (stored) {
    if (partner && stored.data[PARTNER_KEY] !== partner) {
      stored.data[PARTNER_KEY] = partner;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        data: stored.data,
        expiresAt: Date.now() + TTL_MS,
      }));
    }
    return;
  }

  const data: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) data[key] = value;
  }
  if (partner) data[PARTNER_KEY] = partner;
  if (document.referrer) data.referrer = document.referrer;
  if (!Object.keys(data).length) return;

  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    data,
    expiresAt: Date.now() + TTL_MS,
  }));
}

export function getSignupAttribution(): Record<string, string> | undefined {
  if (typeof window === "undefined") return undefined;
  return readStored()?.data;
}

export function getReferringPartnerId(): string | undefined {
  return getSignupAttribution()?.[PARTNER_KEY] || undefined;
}
